import { Box } from '@material-ui/core';
import { getMoneySymbolByCurrency, Pricing, RangeType } from '@nemuru-eng/js-api-client';

import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { Text } from '@/components/shared/components/Text';

import { RangesEditor } from './components/RangesEditor/RangesEditor';
import { RangesEditorParams } from './components/RangesEditor/types/rangesEditorParams';
import {
  MAX_ALLOWED_PERIOD,
  MAX_ALLOWED_PRINCIPAL,
  MIN_ALLOWED_PERIOD,
  MIN_ALLOWED_PRINCIPAL,
  useEditAgentPricings,
} from './hooks/useEditAgentPricings';

interface EditAgentPricingsRangesProps {
  agentId: string;
  agentPricings: Pricing[];
  opened: boolean;
  setOpened: (opened: boolean) => void;
  handleClose: () => void;
}

export const EditAgentPricingsRanges = ({
  agentId,
  opened,
  setOpened,
  handleClose,
  agentPricings,
}: EditAgentPricingsRangesProps) => {
  if (agentPricings.length === 0) return null;

  const {
    loading,
    handleEditAgentPricings,
    minPeriodGreaterThanMaxPeriod,
    minPrincipalGreaterThanMaxPrincipal,
    pricingRanges,
    setPricingRanges,
  } = useEditAgentPricings({
    agentPricings,
    agentId,
  });

  const { getTextIdTranslation } = useTranslation();

  const conditionsToDisableConfirmButton =
    minPeriodGreaterThanMaxPeriod ||
    minPrincipalGreaterThanMaxPrincipal ||
    pricingRanges.periodMin < MIN_ALLOWED_PERIOD ||
    pricingRanges.periodMax > MAX_ALLOWED_PERIOD ||
    pricingRanges.principalMin < MIN_ALLOWED_PRINCIPAL ||
    pricingRanges.principalMax > MAX_ALLOWED_PRINCIPAL;

  const getParamsByType = (type: RangeType): RangesEditorParams<typeof type> => {
    switch (type) {
      case RangeType.PERIOD:
        return {
          type: RangeType.PERIOD,
          minAllowedRange: MIN_ALLOWED_PERIOD,
          maxAllowedRange: MAX_ALLOWED_PERIOD,
          minRange: pricingRanges.periodMin,
          maxRange: pricingRanges.periodMax,
          handleSliderOnChange,
          handleInputOnChange,
          loading,
          minGreaterThanMax: minPeriodGreaterThanMaxPeriod,
          InputProps: { endAdornment: getTextIdTranslation('MONTHS') as string },
        };
      case RangeType.PRINCIPAL:
        return {
          type: RangeType.PRINCIPAL,
          minAllowedRange: MIN_ALLOWED_PRINCIPAL,
          maxAllowedRange: MAX_ALLOWED_PRINCIPAL,
          minRange: pricingRanges.principalMin,
          maxRange: pricingRanges.principalMax,
          handleSliderOnChange,
          handleInputOnChange,
          loading,
          minGreaterThanMax: minPrincipalGreaterThanMaxPrincipal,
          InputProps: { endAdornment: <>{getMoneySymbolByCurrency(agentPricings[0].currency)}</> },
        };
    }
  };

  const handleSliderOnChange = (e, newValue) => {
    setPricingRanges((s) => ({
      ...s,
      [`${e.target.id}Min`]: newValue[0],
      [`${e.target.id}Max`]: newValue[1],
    }));
  };

  const handleInputOnChange = (e) => {
    setPricingRanges((s) => ({ ...s, [e.target.id]: Number(e.target.value) }));
  };

  const onSubmit = async () => {
    await handleEditAgentPricings();
    handleClose();
  };

  return (
    <ActionDialog
      Title={<Text tId="EDIT_AGENT_PRICINGS_RANGES_DIALOG" />}
      Description={<Text tId={'EDIT_AGENT_PRICINGS_RANGES_DIALOG_TEXT'} />}
      MainButton={<Text tId={'EDIT_AGENT_PRICINGS_RANGES_DIALOG_BTN'} />}
      disabled={conditionsToDisableConfirmButton}
      onSubmit={onSubmit}
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      loading={loading}
    >
      <Box pb={2} />
      <RangesEditor params={getParamsByType(RangeType.PERIOD)} />
      <Box pb={4} />
      <RangesEditor params={getParamsByType(RangeType.PRINCIPAL)} />
    </ActionDialog>
  );
};
